import React from 'react';

import SectionsLayout from '@lyearn/form/src/Form/layout/SectionsLayout';
import { useTranslation } from '@lyearn/i18n';
import { ArrowLeftOutline } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { Box, Button, LinkButton, Stack, Typography } from '@lyearn/ui';
import { StepPanel } from '@/components/MultiStep';

import { useMultiStepContext } from '../../MultiStep/context';
import { MultiStepSection } from '../types';

interface MultiStepContentProps {
  sections: MultiStepSection[];
  onNext?: () => void;
  onPrevious?: () => void;
}

export function DefaultSectionRenderer(props: {
  onNext?: () => void;
  onPrevious?: () => void;
  section: MultiStepSection;
}) {
  const { section, onNext, onPrevious } = props;
  const { t } = useTranslation();

  return (
    <StepPanel
      key={`panel-${section.id}`}
      className={cn('flex-1', section.classes?.panel)}
      direction="col"
      id={section.id}>
      {onPrevious ? (
        <LinkButton
          StartIcon={ArrowLeftOutline}
          className="mb-8 self-start ms-8"
          onClick={onPrevious}>
          {t('Common:Actions.Back')}
        </LinkButton>
      ) : null}
      {section.title || section.description ? (
        <Stack className="mb-40 ms-8" direction="col" gapY={4}>
          <Typography variant="display-l">{section.title}</Typography>
          <Typography color="text-text-secondary" variant="body-short-01">
            {section.description}
          </Typography>
        </Stack>
      ) : null}
      <SectionsLayout className="w-full" layout={section.sectionLayout} />
      {onNext && !section.hideNextButton ? (
        <Box className="pr-auto flex-shrink-0 pt-64">
          <Button appearance="secondary" onClick={onNext}>
            {t('Common:Actions.Next')}
          </Button>
        </Box>
      ) : null}
    </StepPanel>
  );
}

const MultiStepContent: React.FC<MultiStepContentProps> = ({ sections, onNext, onPrevious }) => {
  const { activeStepId } = useMultiStepContext();

  return (
    <Stack alignItems="center" className="flex-1" direction="col">
      {sections.map((section, index) => {
        const { renderSection } = section;

        if (activeStepId !== section.id) return null;

        return renderSection ? (
          renderSection({ section, onNext, onPrevious })
        ) : (
          <DefaultSectionRenderer
            key={`panel-${section.id}`}
            section={section}
            onNext={onNext}
            onPrevious={onPrevious}
          />
        );
      })}
    </Stack>
  );
};

export default MultiStepContent;
